<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.activities_index,
                    },
                    {
                        name: $tc('messages.attempts', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.group')" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.students', 1)" for="student_id" />
                        <FormSelect
                            :null-option-text="
                                form.group_id
                                    ? $t('messages.select')
                                    : $t('messages.select_X', { X: $tc('messages.groups', 1) })
                            "
                            id="student_id"
                            v-model="form.student_id"
                            :options="selects.students"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                        <FormSelect
                            id="subject_id"
                            v-model="form.subject_id"
                            :options="options.subjects"
                            class="mt-1 w-full"
                            optgroups
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.activity_mode')" for="mode" />
                        <FormSelect id="mode" v-model="form.mode" :options="options.modes" class="mt-1 w-full" />
                        <FormError :value="form.errors.mode" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_from')" for="date_from" />
                        <FormInput id="date_from" v-model="form.date_from" type="date" name="date_from" class="mt-1" />
                        <FormError :value="form.errors.date_from" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_to')" for="date_to" />
                        <FormInput id="date_to" v-model="form.date_to" type="date" name="date_to" class="mt-1" />
                        <FormError :value="form.errors.date_to" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-12">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1 text-right">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="meta.info.calendar_graph_widget" class="mt-3">
            <AppWell>
                <div>
                    <CalendarChartWidget :widget="meta.info.calendar_graph_widget" @date-clicked="handleDateClicked" />
                </div>
            </AppWell>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="divide-y divide-gray-200 w-full">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs md:w-40 px-6 py-1 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="max-w-xs md:w-40 px-6 py-1 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.activities', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-2 py-1 bg-gray-50 text-xs text-left leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.questions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-2 py-1 bg-gray-50 text-xs text-left leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.speed') }}
                                </th>
                                <th
                                    class="max-w-xs px-2 py-1 bg-gray-50 text-xs text-left leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-2 py-1 bg-gray-50 text-xs text-left leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                ></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(attempt, index) in attempts.data"
                                :key="attempt.id"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-left text-sm leading-5 font-medium text-gray-900"
                                >
                                    <StudentInfoItem :student="attempt.student" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-left text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div>
                                        {{ attempt.title }}
                                    </div>
                                    <div class="flex justify-between">
                                        <div class="text-gray-400">
                                            {{ attempt.mode }}
                                        </div>
                                        <div v-if="attempt.time_away_info">
                                            <Tippy>
                                                <IconZoomCode class="w-6 h-6 text-orange-400" />
                                                <template #content>
                                                    <div class="font-semibold text-gray-200">
                                                        {{ $t('messages.time_away_from_the_quiz') }}
                                                    </div>
                                                    <div class="text-gray-300 mt-3">
                                                        <div class="flex gap-2">
                                                            <IconClock class="w-6 h-6 text-gray-400" />
                                                            <div>
                                                                {{
                                                                    $tc(
                                                                        'messages.X_seconds',
                                                                        attempt.time_away_info.seconds,
                                                                        { count: attempt.time_away_info.seconds },
                                                                    )
                                                                }}
                                                            </div>
                                                        </div>
                                                        <div class="flex gap-2">
                                                            <IconNumber123 class="w-6 h-6 text-gray-400" />
                                                            <div>
                                                                {{
                                                                    $tc(
                                                                        'messages.X_times',
                                                                        attempt.time_away_info.count,
                                                                        { count: attempt.time_away_info.count },
                                                                    )
                                                                }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Tippy>
                                        </div>
                                    </div>
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-left text-sm leading-5 font-medium text-gray-900"
                                >
                                    <span>{{ attempt.correct_answers_count }} / {{ attempt.answers_count }}</span>
                                    <span class="text-gray-500" :title="$t('messages.answers_correct_percent')">
                                        ({{ attempt.score }}%)</span
                                    >
                                </td>

                                <td
                                    class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-left text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ attempt.duration }}
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-left text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div>{{ attempt.date }}</div>
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-left text-sm leading-5 font-medium text-gray-900"
                                >
                                    <InertiaSquareLinkButton action="show" :href="attempt.links.show" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="attempts" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import StudentInfoItem from '@/components/StudentInfoItem.vue';
import axios from 'axios';
import AppWell from '@/Shared/AppWell.vue';
import CalendarChartWidget from '@/components/Widgets/Teacher/CalendarChartWidget.vue';
import { Tippy } from 'vue-tippy';
import { IconClock, IconNumber123, IconZoomCode } from '@tabler/icons-vue';
import FormInput from '../../../../components/Forms/FormInput.vue';

export default {
    components: {
        FormInput,
        Tippy,
        IconZoomCode,
        IconClock,
        IconNumber123,
        CalendarChartWidget,
        AppWell,
        StudentInfoItem,
        FormSelect,
        FormError,
        SubmitFormButton,
        FormLabel,
        InertiaSquareLinkButton,
        InertiaPagination,
        PanelPage,
    },
    layout: Layout,
    name: 'Index',
    props: ['attempts', 'options', 'filters', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                group_id: this.filters.group_id,
                student_id: this.filters.student_id,
                mode: this.filters.mode,
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
            }),
            selects: {
                students: this.options.students,
            },
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        fetchStudents() {
            axios.get(this.route('select.group_students.show', { group: this.form.group_id })).then((response) => {
                this.form.student_id = null;
                this.selects.students = response.data.items;
            });
        },
        handleDateClicked(value) {
            this.form.date_from = value;
            this.form.date_to = value;
            this.filter();
        },
    },
    watch: {
        'form.group_id': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (newVal) {
                        this.fetchStudents();
                    } else {
                        this.form.student_id = null;
                        this.selects.students = [];
                    }
                }
            },
            deep: true,
        },
    },
};
</script>
